import { LogtoClientError, LogtoRequestError } from "@logto/rn";
import { LOGTO_CLIENT, LOGTO_MESSAGE_BUS } from "./session.ctx";
import AsyncStorage from "@react-native-async-storage/async-storage";

export class FetchError extends Error {
  constructor(
    public payload: {
      status: number;
      statusText: string;
      body?: string;
    }
  ) {
    const message = [payload.statusText, payload.body]
      .filter((s) => !!s)
      .join(",");

    super(message);
  }
}

type _HTTPMethod = "get" | "post" | "put" | "delete" | "patch" | "head";
type HTTPMethod = _HTTPMethod | Uppercase<_HTTPMethod>;
type DataType = Record<string, any>;

async function retry<T>(
  fn: () => Promise<T>,
  retries: number = 3,
  delay: number = 1000
): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    if (retries > 0) {
      await new Promise((resolve) => setTimeout(resolve, delay));
      return retry(fn, retries - 1, delay);
    } else {
      throw error;
    }
  }
}

export const clientInstance = async <T>(
  {
    url,
    method,
    params,
    headers,
    data,
    signal,
  }: {
    url: string;
    method: HTTPMethod;
    headers?: any;
    params?: any;
    data?: Promise<DataType> | DataType;
    responseType?: string;
    signal?: any;
  },
  options?: {
    baseUrl?: string;
  }
): Promise<T> => {
  const apiUrlString = `${
    options?.baseUrl ?? process.env.EXPO_PUBLIC_API_BASE_URL
  }${url}`;

  const apiUrl = new URL(apiUrlString);
  if (params && typeof params === "object") {
    for (const [key, value] of Object.entries(params)) {
      apiUrl.searchParams.set(key, value as any);
    }
  }

  const resolvedData = await data;

  const isFormData = resolvedData && resolvedData instanceof FormData;
  let finalHeaders = { ...headers };
  if (isFormData) {
    Object.keys(finalHeaders)
      .filter((k) => k.toLowerCase() === "content-type")
      .forEach((k) => delete finalHeaders[k]);
  }

  if (!finalHeaders["authorization"]) {
    const accessToken = await retry(async () => {
      const kvs = await AsyncStorage.multiGet([
        "impersonation",
        "impersonation-exp",
      ]);
      const impersonation = kvs.find(([k]) => k === "impersonation")?.[1];

      if (impersonation) {
        const exp = kvs.find(([k]) => k === "impersonation-exp")?.[1];
        if (!exp || parseInt(exp) > Date.now()) {
          // Token is not expired or has no expiration
          return impersonation;
        } else {
          await AsyncStorage.multiRemove([
            "impersonation",
            "impersonation-exp",
          ]);
        }
      }

      return LOGTO_CLIENT.getAccessToken(
        LOGTO_CLIENT.logtoConfig.resources?.[0]
      );
    }).catch((e) => {
      if (e instanceof LogtoRequestError && e.code.includes("invalid_grant")) {
        LOGTO_MESSAGE_BUS.publish("SIGN_IN_REQUIRED");
      } else if (
        e instanceof LogtoClientError &&
        e.code === "not_authenticated"
      ) {
        LOGTO_MESSAGE_BUS.publish("SIGN_IN_REQUIRED");
      }
    });
    if (accessToken) {
      finalHeaders["authorization"] = `Bearer ${accessToken}`;
    }
  }

  const response = await fetch(apiUrl, {
    method,
    headers: finalHeaders,
    signal,
    body: isFormData ? resolvedData : JSON.stringify(resolvedData),
  });

  if (response.ok) {
    const contentType = response.headers.get("content-type");
    if (!contentType || contentType.indexOf("application/json") !== -1) {
      return response.json();
    } else {
      return response.text() as any;
    }
  }
  await response.json();

  throw new FetchError({
    status: response.status,
    statusText: response.statusText,
    body: await response.text().catch((_) => undefined),
  });
};

export default clientInstance;
